import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useOutletContext } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { ArrowLeft, ArrowRight, Download, Clock, AlignLeft } from 'lucide-react';
import { api, API, pad } from '../lib/api';
import PaperBody from '../components/paper/PaperBody';
import PaperToc from '../components/paper/PaperToc';
import TemplateSections, { templateTocItems } from '../components/paper/TemplateSections';
import { Label } from '../components/u99/Label';
import Colophon, { Byline, KindEyebrow } from '../components/u99/Colophon';

const NavCard = ({ d, dir }) => (
  <Link to={`/u99/doctrines/${d.slug}`} className={`group flex-1 rounded-2xl border border-white/[0.08] hover:border-amber-300/40 p-6 transition-colors duration-500 ${dir === 'next' ? 'text-right' : ''}`} data-testid={`paper-nav-${dir}`}>
    <p className={`font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-2 ${dir === 'next' ? 'justify-end' : ''}`}>
      {dir === 'prev' && <ArrowLeft size={12} />} Doctrine {pad(d.number)} {dir === 'next' && <ArrowRight size={12} />}
    </p>
    <p className="font-celestial text-lg text-slate-200 group-hover:text-amber-100 mt-3 transition-colors duration-300">{d.title}</p>
  </Link>
);

export default function DoctrinePage() {
  const { slug } = useParams();
  const { scrollTo } = useOutletContext();
  const [doc, setDoc] = useState(null);
  const [all, setAll] = useState([]);
  const [error, setError] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  useEffect(() => {
    setDoc(null);
    setError(false);
    api.get(`/doctrines/${slug}`).then((r) => {
      setDoc(r.data);
      document.title = `${r.data.title} — Doctrines`;
    }).catch(() => setError(true));
    api.get('/doctrines').then((r) => setAll(r.data)).catch(() => {});
  }, [slug]);

  const tocExtra = useMemo(() => {
    if (!doc) return [];
    const items = doc.has_template ? templateTocItems(doc) : [];
    if (doc.has_template && doc.body_md) items.push({ level: 2, text: 'Full text', id: 'full-text' });
    return items;
  }, [doc]);

  const idx = all.findIndex((d) => d.slug === slug);
  const prev = idx > 0 ? all[idx - 1] : null;
  const next = idx >= 0 && idx < all.length - 1 ? all[idx + 1] : null;

  if (error) {
    return (
      <main className="pt-40 pb-40 text-center px-6" data-testid="paper-not-found">
        <p className="font-mono-precision text-xs tracking-[0.3em] text-amber-300">SIGNAL LOST</p>
        <h1 className="font-celestial text-3xl text-slate-100 mt-4">This doctrine is not published</h1>
        <Link to="/u99/doctrines" className="inline-block mt-8 font-mono-precision text-[11px] uppercase tracking-[0.25em] text-slate-400 hover:text-amber-300">← Back to Doctrines</Link>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen" data-testid="doctrine-page">
      <motion.div className="fixed top-16 left-0 right-0 h-px bg-amber-400 origin-left z-40" style={{ scaleX: progress }} data-testid="paper-progress-bar" />
      {doc && (
        <>
          <header className="relative pt-32 pb-14 lg:pt-44 lg:pb-24 nebula-mesh overflow-hidden">
            <span className="absolute -top-6 right-0 lg:right-10 font-celestial text-[38vw] lg:text-[22vw] leading-none text-white/[0.025] select-none pointer-events-none" aria-hidden="true">{pad(doc.number)}</span>
            <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
              <Link to="/u99/doctrines" className="inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.3em] text-slate-500 hover:text-amber-300 transition-colors" data-testid="paper-back-link"><ArrowLeft size={12} /> Doctrines</Link>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="mt-10 flex flex-wrap items-center gap-4">
                <KindEyebrow kind="doctrine" number={doc.number} />
                <Label id={doc.label} size="xs" />
              </motion.div>
              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }} className="mt-6 font-celestial text-4xl sm:text-5xl lg:text-6xl font-light text-slate-100 leading-[1.05] max-w-4xl" data-testid="paper-title">{doc.title}</motion.h1>
              <Byline author={doc.author} />
              {doc.subtitle && <p className="mt-6 text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed" data-testid="paper-subtitle">{doc.subtitle}</p>}
              {doc.abstract && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="mt-10 max-w-3xl font-celestial text-lg sm:text-xl leading-relaxed text-amber-50/80 italic border-l border-amber-400/50 pl-6" data-testid="paper-abstract">{doc.abstract}</motion.p>}
              <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="paper-meta">
                <span className="flex items-center gap-2"><Clock size={12} /> {doc.reading_minutes} min read</span>
                <span className="flex items-center gap-2"><AlignLeft size={12} /> {doc.word_count.toLocaleString()} words</span>
                <span>Updated {new Date(doc.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                {doc.tags?.map((t) => <span key={t} className="text-amber-300/70 border border-amber-400/20 rounded-full px-3 py-0.5">{t}</span>)}
                <a href={`${API}/doctrines/${doc.slug}/markdown`} download className="flex items-center gap-2 text-slate-400 hover:text-amber-300 transition-colors" data-testid="paper-download-md"><Download size={12} /> .md</a>
              </div>
            </div>
          </header>

          <div className="max-w-7xl mx-auto px-5 lg:px-10 pb-28 lg:pb-40">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
              <aside className="hidden lg:block lg:col-span-3"><div className="sticky top-28"><PaperToc markdown={doc.has_template ? '' : doc.body_md} extra={tocExtra} onJump={scrollTo} /></div></aside>
              <article className="lg:col-span-7 border-t border-white/[0.07] pt-14">
                {doc.has_template && <TemplateSections doc={doc} />}
                {doc.body_md && (
                  <div id="full-text" className={doc.has_template ? 'mt-20 pt-14 border-t border-white/[0.07] scroll-mt-28' : ''}>
                    {doc.has_template && <h2 className="font-celestial text-2xl sm:text-3xl text-slate-100 mb-10">Full text</h2>}
                    <PaperBody markdown={doc.body_md} lead />
                  </div>
                )}
                {doc.full_text_path && (
                  <Link to={doc.full_text_path} className="mt-12 flex items-center justify-between gap-4 rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.04] px-6 py-5 hover:bg-emerald-400/[0.08] transition-colors" data-testid="doctrine-full-text-link">
                    <span className="font-celestial text-lg text-slate-100">Read the complete doctrine — published word for word as a standalone page</span>
                    <span className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-emerald-300 whitespace-nowrap">Open full text →</span>
                  </Link>
                )}
                {!doc.body_md && !doc.has_template && <p className="font-mono-precision text-xs tracking-[0.25em] text-slate-500" data-testid="paper-empty">TRANSMISSION INCOMING — THIS DOCTRINE IS BEING TRANSCRIBED</p>}
                <Colophon kind="doctrine" title={doc.title} author={doc.author} date={doc.updated_at} words={doc.word_count} verified={doc.verbatim_verified} path={`/u99/doctrines/${doc.slug}`} backTo="/u99/doctrines" backLabel="All doctrines" />
                <div className="mt-16 pt-10 border-t border-white/[0.07]">
                  <p className="font-mono-precision text-[10px] uppercase tracking-[0.3em] text-slate-600 mb-6">Other doctrines</p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    {prev ? <NavCard d={prev} dir="prev" /> : <div className="flex-1" />}
                    {next ? <NavCard d={next} dir="next" /> : <div className="flex-1" />}
                  </div>
                </div>
              </article>
            </div>
          </div>
        </>
      )}
      {!doc && !error && <div className="pt-48 pb-48 text-center font-mono-precision text-xs tracking-[0.3em] text-slate-600">DECODING…</div>}
    </main>
  );
}
